<template>
  <div>
    <ActitiveTabcomponent :tabs="tabItems" :defaultTab="activeTab" @tab-changed="handleTabChange">
      <template v-slot:tab-buttons="{ activeTab }">
        <div v-if="activeTab === 'Forms'">
          <v-btn class="primary white-text" @click="openAddFormDialog">
            <v-icon left>mdi-plus</v-icon>
            Add</v-btn
          >
        </div>
        <div v-if="activeTab === 'Responses'" class="flex justify-end items-center">
          <div
            class="justify-end gap-4"
            :class="[$vuetify.breakpoint.lgAndUp ? 'flex' : 'flex-col']"
          >
            <!-- Form Name Filter -->
            <v-col
              :cols="$vuetify.breakpoint.lgAndUp ? '' : '12'"
              :class="[$vuetify.breakpoint.lgAndUp ? '' : 'ml-auto']"
            >
              <v-text-field
                v-model="filterForm"
                label="Search Form Name/ Form Id"
                placeholder="Search Form Name/ Form Id"
                solo
                flat
                clearable
                outlined
                dense
                hide-details
                class="filter-column"
                @input="handleInput"
                prepend-inner-icon="mdi-magnify"
              ></v-text-field>
            </v-col>

            <!-- Use Case Filter -->
            <v-col
              :cols="$vuetify.breakpoint.lgAndUp ? '' : '12'"
              :class="[$vuetify.breakpoint.lgAndUp ? '' : 'ml-auto']"
            >
              <v-select
                v-model="filterUseCase"
                label="Select Use Case"
                placeholder="Select Use Case"
                :items="useCaseOptions"
                item-text="text"
                item-value="value"
                solo
                flat
                clearable
                outlined
                dense
                hide-details
                class="filter-column"
                @change="handleInput"
              ></v-select>
            </v-col>

            <!-- Special Category Filter -->
            <v-col
              :cols="$vuetify.breakpoint.lgAndUp ? '' : '12'"
              :class="[$vuetify.breakpoint.lgAndUp ? '' : 'ml-auto']"
            >
              <v-select
                v-model="filterSpecialCategory"
                label="Select Special Category"
                placeholder="Select Special Category"
                :items="specialCategoryOptions"
                item-text="text"
                item-value="value"
                solo
                flat
                clearable
                outlined
                dense
                hide-details
                class="filter-column"
                @change="handleInput"
              ></v-select>
            </v-col>
          </div>
        </div>
      </template>
      <template v-slot:Forms>
        <CategoryQuestionnaire
          :receivedData="receivedData"
          @predefined-keys-fetched="handlePredefinedKeysFetched"
          class="py-4"
          ref="categoryQuestionnaire"
        />
      </template>

      <template v-slot:Responses>
        <div class="overflow-x-auto overflow-y-auto pt-2">
          <v-data-table
            :headers="dynamicHeaders"
            :items="responses"
            item-key="_id"
            class="dynamic-response-table border border-[#e0e0e0]"
            :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
            :options.sync="options"
            :server-items-length="totalItems"
            :loading="loading"
            fixed-header
          >
            <template v-for="(header, index) in dynamicHeaders" v-slot:[`header.${header.value}`]>
              <p
                :key="index"
                class="font-title"
                v-if="header.text?.length > 15"
                v-tooltip="{ text: header.text }"
              >
                {{ header.text }}
              </p>
              <p :key="index" v-else class="font-title">{{ header.text }}</p>
            </template>
            <template v-slot:[`item.responseId`]="{ item }">
              <td class="px-4 py-6 table-font response-id-cell">
                {{ item.responseId || '-' }}
              </td>
            </template>
            <template v-slot:[`item.email`]="{ item }">
              <td class="px-4 py-6 table-font response-id-cell">
                {{ item.metaData?.to?.[0] || '-' }}
              </td>
            </template>
            <template v-slot:[`item.formName`]="{ item }">
              <td class="px-4 py-6 table-font form-name-cell">
                {{ item.linkedFormDetails?.categoryName || '-' }}
              </td>
            </template>
            <template v-slot:[`item.formAccess`]="{ item }">
              <td class="px-4 py-6 table-font">
                <v-chip
                  v-if="item.linkedFormDetails?.isPrivate !== undefined"
                  label
                  :color="getStatusColor(item.linkedFormDetails?.isPrivate)"
                  outlined
                  class="font-semibold"
                  small
                >
                  {{ item.linkedFormDetails?.isPrivate ? 'Private' : 'Public' || '-' }}
                </v-chip>
                <span v-else> - </span>
              </td>
            </template>
            <template v-slot:[`item.formType`]="{ item }">
              <td class="px-4 py-6 table-font form-name-cell">
                <v-chip
                  :color="extractFormType(item.linkedFormDetails)?.color"
                  label
                  small
                  outlined
                  class="font-semibold"
                >
                  {{ extractFormType(item.linkedFormDetails)?.text }}
                </v-chip>
              </td>
            </template>
            <template v-slot:[`item.expiry`]="{ item }">
              <td class="px-4 py-6 table-font">
                <v-chip
                  v-if="getExpiryStatus(item).timeString"
                  label
                  :color="getExpiryStatus(item).isExpired ? 'red' : 'orange'"
                  outlined
                  class="font-semibold"
                  small
                >
                  {{ getExpiryStatus(item).timeString }}
                </v-chip>
                <span v-else> - </span>
              </td>
            </template>
            <template v-slot:[`item.response`]="{ item, index }">
              <td class="px-4 py-6 table-font form-name-cell">
                <v-btn @click="viewResponse(item, index)" small outlined color="primary"
                  >View</v-btn
                >
              </td>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <td class="px-4 py-6 table-font form-name-cell">
                <v-btn
                  @click="createEmployee(item)"
                  small
                  color="primary"
                  :disabled="hasEmployeeCreatedActivity(item)"
                  >Create Employee</v-btn
                >
              </td>
            </template>
          </v-data-table>

          <v-dialog v-model="dialog" max-width="70rem">
            <DynamicFormPage
              :questionResponses="responses[selectedResponseInd]?.questionsAnswered"
              :responseId="responses[selectedResponseInd]?._id"
              :formImages="responses[selectedResponseInd]?.linkedFormDetails?.images"
              :responseHeader="responses[selectedResponseInd]?.linkedFormDetails?.header"
              :initialActivities="responses[selectedResponseInd]?.activities"
              @response-updated="responseUpdatedByAdmin"
            />
          </v-dialog>
        </div>
      </template>

      <template v-slot:EmailLogs>
        <div class="overflow-x-auto overflow-y-auto pt-2">
          <v-data-table
            :headers="emailLogHeaders"
            :items="emailLogs"
            item-key="id"
            class="dynamic-response-table border border-[#e0e0e0]"
            :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
            :options.sync="emailLogOptions"
            :server-items-length="totalEmailLogs"
            :loading="emailLogLoading"
            fixed-header
          >
            <template v-for="(header, index) in emailLogHeaders" v-slot:[`header.${header.value}`]>
              <p
                :key="index"
                class="font-title"
                v-if="header.text?.length > 15"
                v-tooltip="{ text: header.text }"
              >
                {{ header.text }}
              </p>
              <p :key="index" v-else class="font-title">{{ header.text }}</p>
            </template>
            <template v-slot:[`item.timestamp`]="{ item }">
              <td class="px-4 py-6 table-font">
                {{ item.timestamp || '-' }}
              </td>
            </template>
            <template v-slot:[`item.recipient`]="{ item }">
              <td class="px-4 py-6 table-font form-name-cell">
                {{ item.recipient || '-' }}
              </td>
            </template>
            <template v-slot:[`item.subject`]="{ item }">
              <td class="px-4 py-6 table-font form-name-cell">
                {{ item.subject || '-' }}
              </td>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <td class="px-4 py-6 table-font">
                <v-chip
                  label
                  :color="getEmailStatusColor(item.status)"
                  outlined
                  class="font-semibold"
                  small
                >
                  {{ item.status || '-' }}
                </v-chip>
              </td>
            </template>
          </v-data-table>
        </div>
      </template>
    </ActitiveTabcomponent>
  </div>
</template>

<script>
import { convertToTitleCase, debounce, calculateRemainingTime } from '@/utils/common'
import CategoryQuestionnaire from '@/components/Workforce/Settings/OrgConfigComponents/CategeoryQuestionnaire.vue'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'
import DynamicFormPage from '@/pages/Workforce/DynamicFormPage.vue'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'DynamicResponses',
  layout: 'Workforce/default',
  components: {
    CategoryQuestionnaire,
    ActitiveTabcomponent,
    DynamicFormPage
  },

  data() {
    return {
      orgData: this.$storage.getUniversal('orgData'),
      receivedData: null,
      filterForm: '',
      filterUseCase: '',
      filterSpecialCategory: '',
      loading: false,
      responses: [],
      totalItems: 0,
      options: { itemsPerPage: 5, page: 1 },
      useCaseOptions: [],
      specialCategoryOptions: [],
      selectedResponseInd: 0,
      baseHeaders: [
        { text: 'Response ID', value: 'responseId', sortable: false, width: '20%' },
        { text: 'Send To', value: 'email', sortable: false, width: '20%' },
        { text: 'Form Name', value: 'formName', sortable: false, width: '20%' },
        { text: 'Access', value: 'formAccess', sortable: false, width: '10%' },
        { text: 'Type', value: 'formType', sortable: false, width: '10%' },
        { text: 'Expiry', value: 'expiry', sortable: false, width: '10%' },
        { text: 'Response', value: 'response', sortable: false, width: '10%' },
        { text: 'Action', value: 'action', sortable: false, width: '10%' }
      ],
      columnWidth: '150px',
      dialog: false,
      // Email logs data
      emailLogLoading: false,
      emailLogs: [
        {
          id: 1,
          timestamp: '2023-05-15 10:30:00',
          recipient: 'john.doe@example.com',
          subject: 'Form Submission Confirmation',
          status: 'Sent'
        },
        {
          id: 2,
          timestamp: '2023-05-15 11:45:00',
          recipient: 'jane.smith@example.com',
          subject: 'Document Verification Required',
          status: 'Delivered'
        },
        {
          id: 3,
          timestamp: '2023-05-16 09:15:00',
          recipient: 'admin@company.com',
          subject: 'Daily Report',
          status: 'Opened'
        },
        {
          id: 4,
          timestamp: '2023-05-16 14:20:00',
          recipient: 'support@company.com',
          subject: 'User Feedback Submission',
          status: 'Sent'
        },
        {
          id: 5,
          timestamp: '2023-05-17 16:05:00',
          recipient: 'manager@company.com',
          subject: 'Weekly Summary Report',
          status: 'Failed'
        }
      ],
      totalEmailLogs: 5,
      emailLogOptions: { itemsPerPage: 5, page: 1 },
      emailLogHeaders: [
        { text: 'Timestamp', value: 'timestamp', sortable: false, width: '20%' },
        { text: 'Recipient', value: 'recipient', sortable: false, width: '25%' },
        { text: 'Subject', value: 'subject', sortable: false, width: '35%' },
        { text: 'Status', value: 'status', sortable: false, width: '20%' }
      ]
    }
  },
  computed: {
    activeTab() {
      return this.$route.query.activeTab || this.defaultTabKey
    },
    defaultTabKey() {
      return 'Forms'
    },
    tabItems() {
      return [
        {
          key: 'Forms',
          label: 'Forms',
          icon: 'mdi-form-select',
          component: 'CategoryQuestionnaire'
        },
        {
          key: 'Responses',
          label: 'Responses',
          icon: 'mdi-table',
          component: 'ResponsesTable'
        },
        {
          key: 'EmailLogs',
          label: 'Email Logs',
          icon: 'mdi-email-multiple',
          component: 'EmailLogsTable'
        }
      ]
    },
    dynamicHeaders() {
      return [...this.baseHeaders]
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchResponses()
      },
      deep: true
    }
  },
  methods: {
    async handlePredefinedKeysFetched() {
      try {
        const response = await this.$axios.get(`/orgSetup/predefined-special-category`)
        const data = response.data?.data
        this.specialCategoryOptions = data.map((key) => {
          return {
            text: convertToTitleCase(key),
            value: key
          }
        })
      } catch (error) {
        console.error('Error fetching predefined keys:', error)
      }
    },
    async responseUpdatedByAdmin() {
      this.dialog = false
      await this.fetchResponses()
    },
    extractFormType(item) {
      let text = 'Open Ended'
      let color = 'gray'

      switch (true) {
        case !!item.specialCategory:
          text = item.specialCategory
          color = 'blue'
          break
        case !!(item.subCase && item.subCase?._id):
          text = item.subCase.name || ''
          color = 'green'
          break
        default:
          text = 'Open Ended'
          color = 'gray'
      }

      return {
        text,
        color
      }
    },
    viewResponse(item, index) {
      this.selectedResponseInd = index
      this.dialog = true
    },
    handleTabChange(tabKey) {
      if (this.$route.query.activeTab !== tabKey) {
        this.$router.replace({
          query: { ...this.$route.query, activeTab: tabKey }
        })
      }
    },
    openAddFormDialog() {
      // Call the addCategory method from the CategoryQuestionnaire component
      if (this.$refs.categoryQuestionnaire) {
        this.$refs.categoryQuestionnaire.addCategory()
      }
    },

    async fetchResponses() {
      try {
        this.loading = true
        const { page, itemsPerPage } = this.options
        const params = {
          page,
          limit: itemsPerPage > -1 ? itemsPerPage : this.totalItems
        }

        if (this.filterForm) {
          params.search = this.filterForm
        }

        if (this.filterUseCase) {
          params.useCase = this.filterUseCase
        }

        if (this.filterSpecialCategory) {
          params.specialCategory = this.filterSpecialCategory
        }

        const response = await this.$axios.get(`/orgSetup/form-responses`, {
          params
        })

        this.responses = response.data.responses
        // console.log('Fetched responses:', this.responses)
        this.totalItems = response.data.pagination?.totalCount || 0
      } catch (error) {
        console.error('Error fetching responses:', error)
      } finally {
        this.loading = false
      }
    },

    async fetchUseCaseOptions() {
      try {
        const response = await this.$axios.get('/orgSetup/loan-management-field-mapping/data')
        const dataKeys = Object.keys(response.data.data)
        this.useCaseOptions = dataKeys.map((el) => {
          return {
            text: convertToTitleCase(el),
            value: el
          }
        })
      } catch (error) {
        console.error('Error fetching static loan data:', error)
      }
    },

    handleInput: debounce(function () {
      this.options.page = 1
      this.fetchResponses()
    }, 1000),

    getStatusColor(status) {
      const statusColors = {
        true: 'red',
        false: 'green'
      }
      return statusColors[status] || 'grey'
    },

    getEmailStatusColor(status) {
      const statusColors = {
        Sent: 'blue',
        Delivered: 'green',
        Opened: 'purple',
        Failed: 'red'
      }
      return statusColors[status] || 'grey'
    },
    async importEmployees() {
      try {
        await this.$axios.get(axiosUrl.employees.importEmployees)
      } catch (error) {
        console.error('Error importing employees:', error)
      }
    },
    async createEmployee(item) {
      try {
        const payload = await this.constructEmployeePayload(item.questionsAnswered)

        const res = await this.$axios.post('/user/org-employee', payload)

        if (res.data?.success || res.status === 201) {
          const userId = res.data?.data?.user_id
          this.$toast.success('Employee created successfully')
          await this.updateResponseActivity(item)
          this.fetchResponses()
          await this.importEmployees()
          await this.updateEmployee(item, userId)
        }
      } catch (error) {
        console.error('Error creating employee:', error)
        this.$toast.error(error.response?.data?.message || 'Failed to create employee')
      }
    },
    async updateEmployee(item, userId) {
      const payload = await this.constructEmployeePayload(item.questionsAnswered)
      const { department, designation, branch, team, reportesTo, employee_id, salary } = payload

      const data = {
        employee_id,
        department: department || item?.linkedFormDetails?.department,
        designation: designation || item?.linkedFormDetails?.designation,
        branch,
        team,
        salary: Number(salary),
        reportesTo
      }
      try {
        await this.$axios.put(`/user/employeeDetails/${userId}`, data)
      } catch (error) {
        console.error(error)
      }
    },
    hasEmployeeCreatedActivity(item) {
      if (!item.activities || !Array.isArray(item.activities)) return true

      const hasSubmitted = item.activities.some(
        (activity) => activity.activityName === 'USER_FORM_SUBMITTED'
      )
      const hasCreated = item.activities.some(
        (activity) => activity.activityName === 'EMPLOYEE_CREATED'
      )

      return !hasSubmitted || hasCreated
    },

    async updateResponseActivity(item) {
      try {
        const userData = this.$storage.getUniversal('user')
        const newActivity = {
          activityName: 'EMPLOYEE_CREATED',
          updatedBy: userData?._id,
          updatedAt: new Date()
        }

        const currentActivities = item.activities ? [...item.activities] : []
        currentActivities.push(newActivity)

        await this.$axios.put(`/orgSetup/form-response/${item._id}`, {
          activities: currentActivities
        })
      } catch (error) {
        console.error('Error updating response activity:', error)
      }
    },

    getExpiryStatus(item) {
      const expiryDays = item.metaData?.expiry

      if (expiryDays && item?.createdAt) {
        const expiryTime = new Date(item.createdAt).getTime() + expiryDays * 24 * 60 * 60 * 1000
        return calculateRemainingTime(expiryTime)
      }
      return { isExpired: false, timeString: '' }
    },

    constructEmployeePayload(questionsAnswered) {
      const flatData = {}

      const traverse = (items) => {
        if (!items) return
        items.forEach((item) => {
          // Check if it's a leaf node with mappedKeyField
          if (item.mappedKeyField && item.userResponse && item.userResponse.length > 0) {
            flatData[item.mappedKeyField] = item.userResponse[0]
          }

          // Check for subQuestions
          if (item.subQuestions && item.subQuestions.length > 0) {
            traverse(item.subQuestions)
          }
        })
      }

      traverse(questionsAnswered)

      const userData = this.$storage.getUniversal('user')

      // Base payload with organization_id
      const payload = {
        organization_id: userData?.organization_id,
        ...flatData
      }

      // Handle userAddress if address fields are present in flatData
      const addressFields = ['addressLine1', 'addressLine2', 'city', 'state', 'country', 'pinCode']
      const hasAddress = addressFields.some((field) => flatData[field])

      if (hasAddress) {
        payload.userAddress = {
          address_type: 'BILLING',
          addressLine1: flatData.addressLine1 || '',
          addressLine2: flatData.addressLine2 || '',
          city: flatData.city || '',
          state: flatData.state || '',
          country: flatData.country || '',
          pinCode: flatData.pinCode || ''
        }
      } else if (!payload.userAddress) {
        // Ensure userAddress exists as it is required
        payload.userAddress = {
          address_type: 'BILLING',
          addressLine1: '',
          addressLine2: '',
          city: '',
          state: '',
          country: '',
          pinCode: ''
        }
      }

      return payload
    }
  },
  mounted() {
    if (!this.$route.query.activeTab && this.defaultTabKey) {
      this.$router.replace({
        query: { ...this.$route.query, activeTab: this.defaultTabKey }
      })
    }
    this.fetchResponses()
    this.fetchUseCaseOptions()
    this.handlePredefinedKeysFetched()
  }
}
</script>

<style scoped>
.v-data-table {
  max-width: 100%;
  overflow-x: auto;
}

.dynamic-response-table {
  table-layout: fixed;
  border-radius: 0px !important;
}

.response-id-cell {
  width: 150px;
  font-family: monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.form-name-cell {
  width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.question-cell {
  white-space: normal;
  word-break: break-word;
}
.col {
  padding: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.filter-column {
  width: 300px;
}
</style>
